/**
 * 聊天相关类型定义
 */

import { TokenRequest } from "ably";

// 创建私聊参数
export interface CreateDirectChatParams {
  target_id: string;
}

// 未确认消息列表参数
export interface UnackedMessagesParams {
  limit?: number;
  last_evaluated_key?: string;
  from?: string;
  to?: string;
}

// Ably token响应
export interface AblyTokenResult {
  token: TokenRequest;
}

// 获取消息参数
export interface GetMessagesParams {
  limit?: number;
  last_evaluated_key?: string;
  from?: string;
  to?: string;
}

// 对话信息
export interface ConversationInfo {
  id: string;
  group_id: string;
  master_conversation_id: string;
  session_type: SessionType;
  is_pinned: boolean;
  is_muted: boolean;
  is_archived: boolean;
  last_read_at: number | null;
  participants: Participant[];

  // 显示相关字段（组装后添加）
  title?: string;           // 对话标题（好友昵称或群组名称）
  avatar?: string;          // 对话头像
  remark?: string;          // 好友备注（仅私聊）
  alias?: string;           // 好友别名（仅私聊）
  unreadCount?: number;     // 未读消息数量
  lastMessage?: MessageInfo; // 最后一条消息
  isAtSelf?: boolean;        // 是否被AT
}

export interface Participant {
  id: string;
  handle: number;
  role: Role | null;
}

export enum Role {
  Member = "member",
  Owner = "owner",
}

export enum SessionType {
  Direct = "direct",
  Group = "group",
}

// 获取消息响应
export interface MessagesListResult {
  data: MessageInfo[];
  total?: number;
  last_evaluated_key: string;

  // 本地使用的字段
  last_data?: MessageInfo | null;
}

// 消息信息
export interface MessageInfo {
  content: string;
  decryptedContentData?: MessageContentData | undefined;
  sender_id: string;
  deleted_at: string | undefined | null;
  conversation_id: string;
  message_id_time: string | null;
  message_type: number | string;
  key_id: string;
  message_id: string;
  attachments: MessageAttachment[];
  auth_tag: string;
  iv: string;
  receiver_time: number;

  // 发送消息没有下面的字段
  ack?: boolean;
  read?: boolean;
  receiver_id?: string; // 私聊才有
  additional_properties?: Record<string, any>;

  session_type?: SessionType;

  // 显示相关字段（组装后添加）
  messageType?: string | undefined;
  senderName?: string;        // 发送者名称
  senderAvatar?: string;      // 发送者头像
  senderRemark?: string;      // 发送者备注（如果是好友）

  // 群组系统通知类型
  ably_name?: string | undefined;
}

// 消息附件
export interface MessageAttachment {
  type: string;
  url: string;
  size?: number;
  name?: string;
}

// Message 中的 content 字符串字段
// 解密后的数据
export interface MessageContentData {
  // 基础消息体
  id: string;
  content: string;
  status: MessageStatus;
  senderId: string;
  receiverId: string;
  senderName: string;
  timestamp: string;
  attachment?: Attachment | null;
  receiveHandle: string;
  conversationId: string;
  messageType: MessageType;
  showTranslate: boolean;
  translateContent: string;

  // 群组状态消息体

  // 创建群组
  sender_name?: string;
  group_id?: string;

  // 邀请人进群
  member_handles?: string[];
  member_names?: string[];
  invite_id?: string;
  invite_name?: string;

  // 消息更新
  group_update_type?: GroupUpdateType;
  // sender_name?: string;
  group_name?: string;
  group_avatar?: string;
  group_color?: string;

  // 群组成员管理
  // member_handles?: string[]; // 已定义
  // member_names?: string[]; // 已定义
  sender_id?: string; // 用于移除成员通知

  // 群组公告
  notice?: string;

  // 群组置顶消息
  message_id?: string;
  message_content?: string;

  // 群组权限相关
  permissions?: string;

}

export interface Attachment {
  url: string;
  fileName: string;
  fileSize: number;
  fileExtension: string;
  type: AttachmentType;
  uploadStatus?: UploadStatus | null;
  autoDownload: boolean;
  width: number | null;
  height: number | null;
  samples?: number[] | null;
  duration?: number | null;
  contactInfo: ChatContactInfo | null;
  fileId: string | null;
  thumbnail: Thumbnail | null;
  downloadStatus?: AttachmentDownloadStatus | null;
  atUsers: AtUserAttachment[] | null;
}

export enum AttachmentType {
  DOCUMENT = "DOCUMENT",
  IMAGE = "IMAGE",
  AUDIO = "AUDIO",
  VOICE = "VOICE",
  VIDEO = "VIDEO",
  CONTACT = "CONTACT",
  AT = "AT",
}

// 聊天联系人信息接口
export interface ChatContactInfo {
  id: string;
  name?: string;
  avatar?: string;
  phone?: string;
  handle?: string;
  handleRand?: string;
}

// 缩略图信息
export interface Thumbnail {
  id: string;
  url: string;
  name: string;
  width: number;
  height: number;
  file: File;
}

// @ 用户时显示的附件
export interface AtUserAttachment {
  id: string;
  name: string;
  avatar: string;
  handle: string;
  isAll: boolean;
}

// 附件下载状态
export enum AttachmentDownloadStatus {
  NOT_DOWNLOADING = "NOT_DOWNLOADING",
  DOWNLOADING = "DOWNLOADING",
  DOWNLOADED = "DOWNLOADED",
}

// 文件上传状态
export enum UploadStatus {
  NOT_UPLOADING = "NOT_UPLOADING",
  PREPARING = "PREPARING",
  UPLOADING = "UPLOADING",
  UPLOADED = "UPLOADED",
}

export enum GroupUpdateType {
  NAME = 'name',
  AVATAR = 'avatar',
  COLOR = 'color',
  DEFAULT_AVATAR_COLOR = 'default_avatar_color',
  PERMISSIONS = 'permissions',
}

// 消息类型枚举（字符串值）
export enum MessageType {
  // 基础消息类型
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VOICE = 'VOICE',
  VIDEO = 'VIDEO',
  FILE = 'FILE',
  CONTACT = 'CONTACT',
  AT = 'AT',

  // 群组管理消息类型
  GROUP_NEW_CREATE = 'GROUP_NEW_CREATE',
  GROUP_NEW_INVITE = 'GROUP_NEW_INVITE',
  GROUP_INFO_UPDATE = 'GROUP_INFO_UPDATE',
  GROUP_DISSOLVE = 'GROUP_DISSOLVE',
  GROUP_MEMBER_LEAVE = 'GROUP_MEMBER_LEAVE',
  GROUP_MEMBER_ADDED = 'GROUP_MEMBER_ADDED',
  GROUP_MEMBER_REMOVED = 'GROUP_MEMBER_REMOVED',
  GROUP_MEMBER_UPDATED = 'GROUP_MEMBER_UPDATED',
  GROUP_NOTICE_ADD = 'GROUP_NOTICE_ADD',
  GROUP_NOTICE_DELETE = 'GROUP_NOTICE_DELETE',
  GROUP_PINNED_MESSAGE_ADD = 'GROUP_PINNED_MESSAGE_ADD',
  GROUP_PINNED_MESSAGE_DELETE = 'GROUP_PINNED_MESSAGE_DELETE',
  GROUP_MUTE_ENABLED = 'GROUP_MUTE_ENABLED',
  GROUP_MUTE_DISABLED = 'GROUP_MUTE_DISABLED',
  GROUP_VIEW_PROFILE_ENABLED = 'GROUP_VIEW_PROFILE_ENABLED',
  GROUP_VIEW_PROFILE_DISABLED = 'GROUP_VIEW_PROFILE_DISABLED',
  GROUP_VIEW_MEMBER_LIST_ENABLED = 'GROUP_VIEW_MEMBER_LIST_ENABLED',
  GROUP_VIEW_MEMBER_LIST_DISABLED = 'GROUP_VIEW_MEMBER_LIST_DISABLED',
  GROUP_SCREENSHOT_ENABLED = 'GROUP_SCREENSHOT_ENABLED',
  GROUP_SCREENSHOT_DISABLED = 'GROUP_SCREENSHOT_DISABLED',
  GROUP_SEND_CARD_DISABLED = 'GROUP_SEND_CARD_DISABLED',
  GROUP_SEND_CARD_ENABLED = 'GROUP_SEND_CARD_ENABLED',
  GROUP_SEND_IMAGE_DISABLED = 'GROUP_SEND_IMAGE_DISABLED',
  GROUP_SEND_IMAGE_ENABLED = 'GROUP_SEND_IMAGE_ENABLED',
  GROUP_SEND_VIDEO_DISABLED = 'GROUP_SEND_VIDEO_DISABLED',
  GROUP_SEND_VIDEO_ENABLED = 'GROUP_SEND_VIDEO_ENABLED',
  GROUP_SEND_FILE_DISABLED = 'GROUP_SEND_FILE_DISABLED',
  GROUP_SEND_FILE_ENABLED = 'GROUP_SEND_FILE_ENABLED',
}

export enum MessageStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  SEEN = 'SEEN',
  FAILED = 'FAILED'
}